import { displaySlotLabel, istTimeOfDay } from '@pams/shared';
import type { CopyRepeatsSkipReason, FillShareDto } from '@pams/shared';
import { namedOf } from '@/lib/statusPresentation';

/**
 * ⚠️ [COPY-REPEATS] WHY A DATE WAS SKIPPED, ONE PHRASE PER REASON — a `Record` over the server's closed
 * tuple, so a reason added there does not compile here until it has words. None names another
 * division, an order or a roster: the reason is about THIS copy, and nothing else on that date.
 */
const COPY_REPEATS_SKIPPED_BECAUSE: Record<CopyRepeatsSkipReason, string> = {
  OVER: 'already over',
  STARTED: 'already started (a duty with no fixed time starts at midnight)',
  CLOSED_SHORT: 'closed short with a reason',
  MORE_THAN_HALF_NAMED: 'already more than half named',
  DIFFERENT_FORCE: 'asks for a different force on that date',
  NOT_NAMEABLE: 'an officer named here cannot be named on that date',
};

const dates = (count: number): string => `${String(count)} date${count === 1 ? '' : 's'}`;

/**
 * ⚠️ [C3c.2] A `Pick` OF THE WIRE SHAPE, NEVER A HAND-WRITTEN TRIPLE. The dialog's numbers have
 * to be the share's own, so the day a count is renamed on `fillShareSchema` this stops
 * compiling rather than quietly summing something else.
 */
type ShortfallShare = Pick<FillShareDto, 'orgUnit' | 'namedCount' | 'allocatedCount'>;
/** The page's own words. Control-level copy lives in `components/fill/copy.ts`. */
export const DUTY_FILL_COPY = {
  /* ⚠️ [C3c.2] The `sr-only` sentence behind the skeleton — a screen reader is told the same
     thing whichever branch rendered. See the gate in `index.tsx`. */
  loading: 'Loading this duty',
  title: 'Name the officers',
  /**
   * ⚠️ [D4.4 §1a] THE DUTY POINT IS NAMED, AND IT COMES FIRST AFTER THE REFERENCE.
   *
   * The sheet has always shown ONE card's requirement — `planSlots` reads the duty's own
   * stamped lines — but on a three-card order it said only the order reference and a list of
   * places, so a nodal had no way to tell this share from the order's whole requirement.
   *
   * ⚠️ THE PLACE IS DROPPED WHEN IT IS THE SAME WORDS, for D4.2's reason one level down:
   * `dutyPointDisplayTitle` derives the title FROM the places whenever the card is unnamed,
   * so printing both would render "Esplanade · Esplanade" in the common case.
   */
  /**
   * ⚠️ [PRE-P6.2] SHOWN WHEN A ROW FILLS AND THE SHEET IS NOT YET FULL — never when it is.
   * "Proceed to select the next slot" is wrong advice when there is no next slot, and the
   * footer already says so in green.
   */
  rowFilled: 'Every slot has been named.',
  rowFilledHint: 'Please proceed to select the next slot.',
  copyFromLast: 'Copy from last time',
  copyingFrom: (reference: string, date: string, count: number): string =>
    `${String(count)} name${count === 1 ? '' : 's'} filled in from ${reference} on ${date}. Nothing is saved yet.`,
  copyNothing: 'There is no earlier duty on this order to copy from.',
  copyNothingToFill: 'Every slot is already named.',
  discard: 'Discard',
  unsavedGuard: 'These names have not been saved yet. Leave anyway?',
  saveStaged: (count: number): string => `Save ${String(count)} name${count === 1 ? '' : 's'}`,
  /* [C5b §3] `counted` moved to `lib/statusPresentation.ts` (`namedOf`, through `fillFooter`) — the
     one spelling of a fill count on every surface. */

  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C3c.1 §6a] `Mark complete` AND ITS SHORTFALL DIALOG ARE GONE, AND SO IS A CAPABILITY.
   *
   *  §6a: completion already happens automatically when the last officer is named (FR-5.3.6
   *  closes OD-6 that way), so the control was `disabled` at exactly the state it described.
   *
   *  ⚠️ AND IT REMOVED THE SHORTFALL PATH. [P4] let a division complete at PARTIAL **with a
   *  reason**, which is how it recorded that it could not supply the force asked for.
   *
   *  ⚠️⚠️ [amended C3c.2] AND THE CLIENT HAS ASKED FOR IT BACK, SO THAT PARAGRAPH'S "NOTHING
   *  REPLACES IT" IS SUPERSEDED. `Complete With Short-Fall` is below. C3c.1's prediction held
   *  exactly: the server was untouched, so restoring it was a button and a modal against a
   *  contract that never moved and NO migration — what it did cost was a new capability, because
   *  the audience came back WIDER than the one that lost it (see `mayCompleteShortfall`).
   *
   *  ⚠️ IT IS NOT `Mark complete` RETURNING. That control was `disabled` at exactly the state it
   *  described, because FR-5.3.6 auto-completes at full; this one is only ever offered while
   *  something is still OPEN, and it requires the reason that made the old path worth having.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  sendNotification: 'Send Notification',
  notifySent: (count: number): string =>
    count === 1 ? '1 officer has been told' : `${String(count)} officers have been told`,
  notifyNotFull: 'Everyone has to be named before the officers can be told.',
  notifyNothingAsked: 'This duty asks nothing of your unit.',
  /**
   * ⚠️ [C3c.2] THE FULLY-EXCUSED CASE, WHICH THE SERVER REFUSES AND NOTHING USED TO SAY.
   *
   * A share closed at 0 of 10 with a reason excuses every slot on it, so "everyone who has to
   * be named is named" becomes vacuously true — and a send would spend the hour's rate slot,
   * write no rows and report "0 officers have been told" as a success. `notifyNamedOfficers`
   * throws for it; this is the sentence the button says first.
   */
  notifyNobodyNamed: 'Nobody is named on this duty yet.',

  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  [COPY-REPEATS] COPY TO OTHER DATES — this duty's names onto the same duty point's other dates.
   *
   *  ⚠️ THE CONFIRMATION SAYS WHAT IS REPLACED AND WHAT IS SKIPPED BEFORE THE CLICK, because the copy
   *  WRITES other duties — and that nobody is told, because "names copied" reads like "officers told".
   *  The answer then lists every date it reached, copied or skipped and why: a bulk write across five
   *  nights that says nothing is one nobody can check.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  copyRepeats: 'Copy to other dates',
  copyRepeatsTitle: 'Copy these names to other dates',
  copyRepeatsReady: (dutyPointTitle: string, named: number, total: number): string[] => [
    `${namedOf(named, total)} here. This copies them to every other date of ${dutyPointTitle}, at the same time of day.`,
    'Only the names on this screen are copied, each into the same row on that date; another division’s names are never touched. On each date, the names already in those rows are replaced by these.',
    'A date is skipped if it has started or is over, if it is already more than half named, or if it was closed short. You will see every date and what happened to it. Nobody is notified.',
  ],
  copyRepeatsBelow: (percent: number, named: number, total: number, needed: number): string =>
    `At least ${String(percent)}% of this duty has to be named before its names can be copied to other dates. ${namedOf(named, total)}; ${String(needed)} more to go.`,
  copyRepeatsConfirm: 'Copy names',
  copyRepeatsCancel: 'Cancel',
  copyRepeatsCopied: (count: number): string => `Copied to ${dates(count)}`,
  copyRepeatsSkipped: (count: number, reason: CopyRepeatsSkipReason): string =>
    `${dates(count)} skipped: ${COPY_REPEATS_SKIPPED_BECAUSE[reason]}`,
  copyRepeatsDoneTitle: (count: number): string => `Names copied to ${dates(count)}`,
  copyRepeatsNoneTitle: 'No names were copied',
  copyRepeatsNoDatesTitle: 'No other dates',
  copyRepeatsNoDates: (dutyPointTitle: string): string =>
    `${dutyPointTitle} runs on no other date at this time of day, so there was nothing to copy to.`,
  copyRepeatsNobodyTold: 'Nobody has been notified. Send Notification on each date once it is ready.',
  copyRepeatsDone: 'Done',
  /** Only while the sheet has not loaded — the server always names the card. */
  copyRepeatsThisDutyPoint: 'this duty point',

  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C3c.2] COMPLETE WITH SHORT-FALL — the words for the act §6a took away.
   *
   *  ⚠️ THE REASON IS REQUIRED, WHERE THE SWAP REASON ABOVE IS NUDGED. `completeShareSchema`
   *  says `.min(1)`, and the two are genuinely different: a swap's reason is a courtesy to
   *  whoever reads the order later, and this one IS the record — `shortfall_reason` is the only
   *  thing distinguishing "we closed at seven because we could not send ten" from "we closed at
   *  seven", and after this pass it is also what lets the officers who ARE coming be told.
   *
   *  ⚠️ AND THE INTRO SAYS WHAT IT COSTS. Completion is not reversible from this screen, and the
   *  count is what the order's own totals will report from then on.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  completeShortfall: 'Complete With Short-Fall',
  shortfallTitle: 'Complete with a short-fall',
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ IT NAMES THE SHARES THE CLICK WILL CLOSE, AND COUNTS **THEIR** SLOTS — NOT THE SHEET'S.
   *
   *  The first version read `(named, total)` off `countNamed`/`countSlots`, which are
   *  `slotSequence` reductions over EVERY line and allocation the sheet holds. Two separate ways
   *  that is a number about a different set:
   *
   *  ⚠️ THE SHEET CAN SPAN DIVISIONS. `completeShares` narrows to `input.divisionOrgUnitId` and
   *  closes every open share it can see when that is absent — and only the INBOX adds
   *  `?division=`. `pages/ArrangementView`, `pages/ArrangementDetail` and the dashboard's
   *  `ARRANGEMENTS_TODAY` row all link to a bare `/duties/:id`, so a CP-office nodal arriving
   *  from any of the three gets one sentence about one share over an act on four.
   *
   *  ⚠️ AND IT MISSES EVEN ON THE NARROWED ROUTE. A share is minted per ADDRESSEE — the UNIT
   *  wherever a line names one (`addresseeOf`) — while `narrowToDivision` filters on
   *  `division_org_unit_id`. So a division that has delegated to its stations holds SEVERAL
   *  shares under one division id, and an ordinary nodal saw the division-wide fraction while
   *  the click closed each station's share separately, every one stamped with the same reason.
   *
   *  So the counts are summed from `fillShareSchema`'s own `namedCount`/`allocatedCount`, over
   *  exactly the rows `hasOpenShare` tested — the sentence and the button's enabled state cannot
   *  disagree, and the number in the dialog is the number the audit row will carry.
   *
   *  ⚠️ AND THE POSSESSIVE IS GONE. A super admin closing North's share does not own it, so the
   *  share NAMES itself. `orgUnit` is nullable on the wire, so the fallback is the neutral noun
   *  rather than a blank.
   *
   *  ⚠️ THE LAST CLAUSE IS NOT A FLOURISH. `allocation_fills.completed_at` has exactly TWO
   *  writers in the whole API — `completeShares` and `autoCompleteFullShares` — and NOTHING
   *  anywhere sets it back to `null`. No un-complete endpoint, no restore, no lifecycle tick. An
   *  operator who closes the wrong duty short has no route back from any screen in the product.
   *
   *  ⚠️ [amended C5b §8a] STILL TRUE OF WHAT THIS DIALOG DOES, AND NO LONGER TRUE OF THE COLUMN.
   *  `recountShares` now clears an AUTOMATIC lock whose names were taken off — a cache nobody
   *  decided. A close with a REASON, which is the only thing this dialog writes, is never touched,
   *  so "this cannot be undone" is exactly as true as it was.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  shortfallIntro: (openShares: readonly ShortfallShare[]): string => {
    const named = openShares.reduce((running, share) => running + share.namedCount, 0);
    const allocated = openShares.reduce((running, share) => running + share.allocatedCount, 0);
    const names = openShares.map((share) => share.orgUnit?.name ?? 'an unnamed unit');
    /* ⚠️ Three names is where a sentence stops being readable; beyond it the COUNT is the
       fact worth stating, and the rows themselves are on the screen behind the dialog. */
    const who =
      names.length === 1
        ? `${names[0] ?? 'this unit'}\u2019s share of this duty`
        : names.length <= 3
          ? `${String(names.length)} shares on this duty — ${names.join(', ')} —`
          : `all ${String(names.length)} open shares on this duty`;
    return [
      `This closes ${who} at ${String(named)} of ${String(allocated)}${names.length === 1 ? '' : ' between them'}.`,
      'The remaining positions stop being asked for, and the officers already named can then be told.',
      'It cannot be undone from here.',
    ].join(' ');
  },
  shortfallLabel: 'Why is the force short?',
  shortfallPlaceholder: 'Only 7 of the 10 U/A men asked for are available.',
  shortfallConfirm: 'Complete with short-fall',
  shortfallDone: 'The short-fall has been recorded.',
  /**
   * ⚠️ The 409 `completeShares` answers, said before the click rather than after it.
   *
   * ⚠️ [amended C3c.2] "EVERY SHARE ON THIS DUTY" WAS AN OVERSTATEMENT, AND IT WAS COPIED FROM
   * THE SERVER'S OWN MESSAGE. `hasOpenShare` reads `data.shares`, which `getFillSheet` has
   * already narrowed to `?division=` — so on a super admin drilled into one division whose rows
   * are closed while another division's are still open, both the tooltip and the 409 claimed
   * something false about the duty. Both sentences are scoped to what was actually checked, and
   * they are changed TOGETHER so the pre-click and post-click wording stay identical.
   */
  shortfallNothingOpen: 'Every share you can act on here is already complete.',
  /**
   * ⚠️ [C3c §5c] THE ADDRESSEE'S OBLIGATION, NOT THE DUTY'S STATE. It says "your part", because
   * the duty may still be short — a division that has named some and handed the rest to its
   * stations has finished what was asked of IT, and [UX1 §3]'s whole point is that those are two
   * different questions with two different answers.
   */
  discharged: 'Your part is done',
  /**
   * ⚠️ [C6a §4] AN AWAITED COMMAND ROLE, IN WORDS — shown whenever one is awaited, a locked force
   * share included, because `🔒 All Filled` over an unnamed In-charge reads as done. "Still to be
   * named" when it is the reader's own to name; "awaited from CD · TALA" when it sits with somebody
   * else. Never a number: one sentence per role and addressee, however many positions are empty.
   */
  commandStillToName: (role: string): string => `${role} still to be named`,
  commandAwaitedFrom: (role: string, from: string): string => `${role} awaited from ${from}`,

  swapTitle: 'Why is this changing?',
  /** ⚠️ Optional, and nudged rather than required (FR-5.3.7). A wall produces "-". */
  swapIntro: 'This share is already marked complete. A short reason helps whoever reads the order later.',
  swapLabel: 'Reason (optional)',
  swapPlaceholder: 'Reported sick',
  swapSkip: 'Swap without a reason',
  swapConfirm: 'Swap',

  /**
   * ⚠️ [C2a §5b] WHICH DUTY IS BEING FILLED — the identity line [PRE-P6.2] removed.
   *
   * `paNumber` is the operator's own order number and leads; `reference` is the system's and
   * is the fallback, because `paNumber` is nullable on plenty of orders. The duty point's own
   * title follows, so an order with several cards says which card this is.
   */
  identity: (duty: {
    reference: string;
    paNumber: string | null;
    displayTitle: string;
    dutyPointTitle: string | null;
    slotLabel: string;
    hasFixedTime: boolean;
    reportingAt: string | null;
    standDownAt: string | null;
  }): string =>
    [
      duty.paNumber ?? duty.reference,
      duty.displayTitle,
      duty.dutyPointTitle,
      /* ⚠️ [C3a §6, amended C5 §4a] `From 3:00 PM` on a whole-day duty that has a reporting
         time, `3:00 PM–9:00 PM` with a stand-down time, and `Whole day` with neither. The
         `humaniseTimes` ordering lives inside `displaySlotLabel`. */
      displaySlotLabel({
        slotLabel: duty.slotLabel,
        hasFixedTime: duty.hasFixedTime,
        reportingTime: duty.reportingAt === null ? null : istTimeOfDay(duty.reportingAt),
        standDownTime: duty.standDownAt === null ? null : istTimeOfDay(duty.standDownAt),
      }),
    ]
      .filter((part): part is string => typeof part === 'string' && part.length > 0)
      .join(' · '),

  /** ⚠️ [C2a §5e] Removing a split is a real act with a consequence, so it confirms. */
  removeDelegationTitle: 'Remove this unit from the split?',
  removeDelegationIntro:
    'The count returns to your own row, and this unit stops seeing the duty. Officers they have already named are kept and will be shown as no longer required.',
  removeDelegationConfirm: 'Remove',
  delegationRemoved: 'Removed from the split',

  droppedHeading: 'No longer required',
  droppedIntro: 'The order changed and these slots were withdrawn. Nobody has told these officers.',
  /**
   * ⚠️ [C3c §3d] IT WAS DEAD, AND THIS PASS IS WHAT REVIVES IT. Nothing in `web/src` referenced
   * `DUTY_FILL_COPY.windowClosed`: the string existed and the surface did not, so a closed duty
   * looked exactly like an open one on this screen. The two windows now render from
   * `FILL_WINDOW_COPY` in `components/fill/copy.ts`, one sentence each, so this page holds no
   * third copy of either. Kept as a pointer rather than deleted silently.
   */
  empty: 'Nothing on this duty is addressed to your division.',
} as const;
